import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { InputText, InputTextType } from 'src/components/atoms/InputText';
import { RadioButtonData } from 'src/components/atoms/RadioButton';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import { RadioButtonGroup } from 'src/components/molecules/RadioButtonGroup';
import Modal, { ModalVariant, ModalWidth } from 'src/components/organisms/Modal';
import { PlatformFeedbackCategory, PlatformFeedbackReviewOutcome } from 'src/features/myWork/api';
import { PlatformFeedbackReport } from 'src/features/myWork/hooks/useReportPlatformFeedback';

import {
  canCompleteOptions,
  CONFIRMATION_DURATION_MS,
  FEEDBACK_MAX_LENGTH,
  categoryOptions
} from './constants';
import { Container, FieldContainer } from './styles';

type ReportPlatformFeedbackModalProps = {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  onSubmit: (
    report: Omit<PlatformFeedbackReport, 'proofId'>
  ) => Promise<PlatformFeedbackReviewOutcome | null>;
  onConfirmationClosed: (review: Exclude<PlatformFeedbackReviewOutcome, 'kept'>) => void;
};

const ReportPlatformFeedbackModal: FC<ReportPlatformFeedbackModalProps> = ({
  showModal,
  setShowModal,
  isLoading,
  onSubmit,
  onConfirmationClosed
}) => {
  const [options, setOptions] = useState<RadioButtonData[]>(categoryOptions);
  const [completeOptions, setCompleteOptions] = useState<RadioButtonData[]>(canCompleteOptions);
  const [step, setStep] = useState<'form' | 'released' | 'already_released'>('form');
  const methods = useForm<{ feedback: string }>({
    mode: 'onChange',
    defaultValues: { feedback: '' }
  });

  const category = useMemo(
    () => options.find((option) => option.isChecked)?.value as PlatformFeedbackCategory | undefined,
    [options]
  );
  const canCompleteAnswer = completeOptions.find((option) => option.isChecked)?.value;
  const feedback = methods.watch('feedback');
  const isFormComplete = !!category && feedback.trim().length > 0 && !!canCompleteAnswer;

  useEffect(() => {
    if (step === 'form') return;

    const timerId = setTimeout(() => onConfirmationClosed(step), CONFIRMATION_DURATION_MS);
    return () => clearTimeout(timerId);
  }, [step, onConfirmationClosed]);

  const resetForm = () => {
    methods.reset();
    setOptions(categoryOptions);
    setCompleteOptions(canCompleteOptions);
  };

  const handleSubmit = methods.handleSubmit(async (values) => {
    if (!category || !canCompleteAnswer) return;

    const review = await onSubmit({
      category,
      feedback: values.feedback.trim(),
      canCompleteReview: canCompleteAnswer === 'yes'
    });
    if (!review) return;

    if (review === 'kept') {
      resetForm();
      setShowModal(false);
    } else {
      setStep(review);
    }
  });

  if (step !== 'form') {
    return (
      <Modal
        title="Feedback reported"
        text={
          step === 'released'
            ? 'Thanks for the report. Taking you back to My Work.'
            : 'Thanks for the report. This review was already handed back, so we are taking you back to My Work.'
        }
        variant={ModalVariant.none}
        width={ModalWidth.small}
        showModal={showModal}
        isBackClosable={false}
        showModalActions={false}
      />
    );
  }

  return (
    <Modal
      title="Report a platform bug"
      variant={ModalVariant.none}
      width={ModalWidth.small}
      showModal={showModal}
      setShowModal={setShowModal}
      isBackClosable={false}
      isEscapeClosable
      showCloseButton
      labelButtonCancel="Cancel"
      labelButtonConfirm="Submit"
      // The shared Button shows a spinner while loading but stays clickable,
      // so loading also disables it to stop a double submit.
      isDisabledButtonConfirm={!isFormComplete || isLoading}
      isLoadingButtonConfirm={isLoading}
      onConfirm={handleSubmit}
      onCancel={resetForm}
    >
      <FormProvider {...methods}>
        <Container>
          <Text variant={TextVariant.normal} color={TextColor.gray600}>
            Your feedback will be reviewed by the Snappt team.
          </Text>

          <FieldContainer>
            <Text variant={TextVariant.small} color={TextColor.gray700} isBold>
              What is the feedback?
              <Text variant={TextVariant.small} color={TextColor.error} isBold>
                {/* A non-breaking space, because Text is a flex box and drops plain spaces. */}
                &nbsp;*
              </Text>
            </Text>
            <RadioButtonGroup
              name="platform-feedback-category"
              data={options}
              onChange={setOptions}
            />
          </FieldContainer>

          <FieldContainer>
            <Text variant={TextVariant.small} color={TextColor.gray700} isBold>
              Feedback
              <Text variant={TextVariant.small} color={TextColor.error} isBold>
                &nbsp;*
              </Text>
            </Text>
            <InputText
              name="feedback"
              placeholder="Type what happened here..."
              type={InputTextType.text}
              isDisabled={isLoading}
              showLabel={false}
              showOptionalLabel={false}
              multiline
              rows={6}
              maxLength={FEEDBACK_MAX_LENGTH}
              config={{
                required: 'Feedback is required',
                validate: {
                  isEmpty: (value) => value?.trim().length > 0 || 'Feedback is required'
                }
              }}
              data-testid="platform-feedback-text"
            />
          </FieldContainer>

          <FieldContainer>
            <Text variant={TextVariant.small} color={TextColor.gray700} isBold>
              Can you complete the review?
              <Text variant={TextVariant.small} color={TextColor.error} isBold>
                &nbsp;*
              </Text>
            </Text>
            <RadioButtonGroup
              name="platform-feedback-can-complete"
              data={completeOptions}
              onChange={setCompleteOptions}
            />
          </FieldContainer>
        </Container>
      </FormProvider>
    </Modal>
  );
};

export default ReportPlatformFeedbackModal;
